import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  basic: {
    firstName: '',
    middleName: '',
    lastName: '',
    gender: '',
    maritalStatus: '',
    dob: '',
    email: '',
    phone: '',
    alternatePhoneNum: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    status: '',
  },
  professional: {
    jobTitle: '',
    salesPerson: '',
    technology: '',
    workPermitStatus: '',
    workStatusIssueDate: '',
    workStatusEndDate: '',
    SSN: '',
    employmentStatus: '',
    referredBy: '',
  },
  profile: {
    totalInterviews: '',
    activeInterviews: '',
    passedInterviews: '',
    failedInterviews: '',
    holdInterviews: '',
    lastInterviewOn: '',
    rating: 0,
  },
  emergencyContacts: {
    contactInfo1: {
      firstName: '',
      lastName: '',
      contactPhoneNum: '',
      email: '',
      contactRelation: '',
      createdBy: '',
      dateCreated: '',
      updatedBy: '',
      lastUpdated: '',
    },
    contactInfo2: {
      firstName: '',
      lastName: '',
      contactPhoneNum: '',
      email: '',
      contactRelation: '',
      createdBy: '',
      dateCreated: '',
      updatedBy: '',
      lastUpdated: '',
    },
  },
  candidateId: '',
  audit: {
    createdBy: '',
    dateCreated: '',
    updatedBy: '',
    lastUpdated: '',
  },
};

const personalSlice = createSlice({
  name: 'personal',
  initialState,
  reducers: {
    setBasicInfo: (state, action) => {
      // Helper to format phone number as (XXX) XXX XXXX
      function formatPhoneNumber(phone) {
        if (!phone) return '';
        // Remove all non-digit characters
        const cleaned = ('' + phone).replace(/\D/g, '');
        if (cleaned.length === 10) {
          // US 10-digit
          return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)} ${cleaned.slice(6, 10)}`;
        } else if (cleaned.length === 11 && cleaned[0] === '1') {
          // US 11-digit with country code
          return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)} ${cleaned.slice(7, 11)}`;
        } else if (cleaned.length === 12 && cleaned.startsWith('91')) {
          // India 12-digit (country code 91)
          return `(${cleaned.slice(2, 5)}) ${cleaned.slice(5, 8)} ${cleaned.slice(8, 12)}`;
        }
        // Fallback: return as is
        return phone;
      }
      state.basic.firstName = action.payload.firstName;
      state.basic.middleName = action.payload.middleName;
      state.basic.lastName = action.payload.lastName;
      state.basic.gender = action.payload.gender;
      state.basic.maritalStatus = action.payload.maritalStatus;
      state.basic.dob = action.payload.dob;
      state.basic.email = action.payload.email;
      state.basic.phone = formatPhoneNumber(action.payload.phone);
      state.basic.alternatePhoneNum = formatPhoneNumber(action.payload.alternatePhoneNum);
      state.basic.address1 = action.payload.address1;
      state.basic.address2 = action.payload.address2;
      state.basic.city = action.payload.city;
      state.basic.state = action.payload.state;
      state.basic.zip = action.payload.zip;
      state.basic.status = action.payload.status;
    },
    setProfessionalInfo: (state, action) => {
      state.professional.jobTitle = action.payload.jobTitle;
      state.professional.salesPerson = action.payload.salesPerson;
      state.professional.technology = action.payload.technology;
      state.professional.workPermitStatus = action.payload.workPermitStatus;
      state.professional.SSN = action.payload.SSN;
      state.professional.workStatusIssueDate = action.payload.workStatusIssueDate;
      state.professional.workStatusEndDate = action.payload.workStatusEndDate;
      state.professional.employmentStatus = action.payload.employmentStatus;
      state.professional.referredBy = action.payload.referredBy;
    },
    setProfileInfo: (state, action) => {
      state.profile.totalInterviews = +action.payload.totalInterviews;
      state.profile.activeInterviews = +action.payload.activeInterviews;
      state.profile.passedInterviews = +action.payload.passedInterviews;
      state.profile.failedInterviews = +action.payload.failedInterviews;
      state.profile.holdInterviews = +action.payload.holdInterviews;
      state.profile.lastInterviewOn = action.payload.lastInterviewOn;
      state.profile.rating = action.payload.rating;
    },
    setEmergencyContacts: (state, action) => {
      // set contact1
      state.emergencyContacts.contactInfo1.firstName = action.payload.contact1.firstName;
      state.emergencyContacts.contactInfo1.lastName = action.payload.contact1.lastName;
      state.emergencyContacts.contactInfo1.contactPhoneNum =
        action.payload.contact1.contactPhoneNum;
      state.emergencyContacts.contactInfo1.email = action.payload.contact1.email;
      state.emergencyContacts.contactInfo1.contactRelation =
        action.payload.contact1.contactRelation;
      if (action.payload.contact1.createdBy !== undefined) {
        state.emergencyContacts.contactInfo1.createdBy = action.payload.contact1.createdBy || '';
        state.emergencyContacts.contactInfo1.dateCreated = action.payload.contact1.dateCreated || '';
        state.emergencyContacts.contactInfo1.updatedBy = action.payload.contact1.updatedBy || '';
        state.emergencyContacts.contactInfo1.lastUpdated = action.payload.contact1.lastUpdated || '';
      }

      // set contact2
      state.emergencyContacts.contactInfo2.firstName = action.payload.contact2.firstName;
      state.emergencyContacts.contactInfo2.lastName = action.payload.contact2.lastName;
      state.emergencyContacts.contactInfo2.contactPhoneNum =
        action.payload.contact2.contactPhoneNum;
      state.emergencyContacts.contactInfo2.email = action.payload.contact2.email;
      state.emergencyContacts.contactInfo2.contactRelation =
        action.payload.contact2.contactRelation;
      if (action.payload.contact2.createdBy !== undefined) {
        state.emergencyContacts.contactInfo2.createdBy = action.payload.contact2.createdBy || '';
        state.emergencyContacts.contactInfo2.dateCreated = action.payload.contact2.dateCreated || '';
        state.emergencyContacts.contactInfo2.updatedBy = action.payload.contact2.updatedBy || '';
        state.emergencyContacts.contactInfo2.lastUpdated = action.payload.contact2.lastUpdated || '';
      }
    },
    setCandidateId: (state, action) => {
      state.candidateId = action.payload;
    },
    setAuditInfo: (state, action) => {
      state.audit.createdBy = action.payload.createdBy || '';
      state.audit.dateCreated = action.payload.dateCreated || '';
      state.audit.updatedBy = action.payload.updatedBy || '';
      state.audit.lastUpdated = action.payload.lastUpdated || '';
    },

    resetState: (state, action) => initialState,
  },
});

export const {
  setBasicInfo,
  setProfessionalInfo,
  setProfileInfo,
  setEmergencyContacts,
  setCandidateId,
  setAuditInfo,
  resetState,
} = personalSlice.actions;

export default personalSlice.reducer;
